/* Warranty registration — owners-pass layout (sections/warranty-owners-pass.liquid).
 *
 * Three steps (gear → owner → email preference) over the same Apps Script
 * contract as the classic form: getCatalog for the series/product lists,
 * one product per submit, submissionId for idempotent retries.
 *
 * ES5 on purpose, like the classic form: no build step in this theme.
 */
(function () {
  'use strict';

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;   // identical to the backend's
  var MIN_DATE = '2010-01-01';
  var MAX_OPTIONS = 100;
  var CATALOG_TIMEOUT_MS = 10000;
  var SUBMIT_TIMEOUT_MS = 20000;
  var CACHE_PREFIX = 'wop:catalog:';
  var CACHE_POINTER = 'wop:catalog:current';

  var PH = {
    loading: 'Loading your options…',
    series: 'Type to search your series',
    product: 'Type to search your product',
    pickSeries: 'Choose your series first'
  };

  /* ── utilities ──────────────────────────────────────────────────────── */

  function each(nodes, fn) {
    for (var i = 0; i < nodes.length; i++) fn(nodes[i], i);
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function todayISO() {
    var d = new Date();
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }
  function trim(str) { return String(str == null ? '' : str).replace(/^\s+|\s+$/g, ''); }
  function closest(el, selector) {
    while (el && el.nodeType === 1) {
      if ((el.matches || el.msMatchesSelector || el.webkitMatchesSelector).call(el, selector)) return el;
      el = el.parentNode;
    }
    return null;
  }
  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }
  function newUuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var t = Date.now();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (t + Math.random() * 16) % 16 | 0;
      t = Math.floor(t / 16);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  /* Folds inch marks and punctuation so `8"` finds `8 inch`. */
  function normalize(str) {
    return String(str == null ? '' : str)
      .toLowerCase()
      .replace(/[″“”"]/g, ' inch ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\binches\b/g, 'inch')
      .replace(/\s+/g, ' ')
      .replace(/^ | $/g, '');
  }

  /* Height of the theme's sticky header while it is stuck; 0 otherwise. */
  function stickyHeaderBottom() {
    var header = document.querySelector('.site-header--stuck');
    if (!header) return 0;
    var bottom = header.getBoundingClientRect().bottom;
    return bottom > 0 ? Math.round(bottom) : 0;
  }
  function stickyHeaderHeight() {
    var stuck = document.querySelector('.site-header--stuck');
    if (stuck) return stuck.offsetHeight;
    var sticky = document.querySelector('.site-header[data-sticky="true"]');
    return sticky ? sticky.offsetHeight : 0;
  }

  function scrollToY(y) {
    y = Math.max(0, Math.round(y));
    try {
      window.scrollTo({ top: y, behavior: reducedMotion() ? 'auto' : 'smooth' });
    } catch (e) {
      window.scrollTo(0, y);
    }
  }

  /* ── searchable combobox ──────────────────────────────────────────────
     Same behaviour as the classic form's: typing filters without
     committing; only choose() changes the selection. */
  function createCombobox(wrap, opts) {
    var input = wrap.querySelector('[data-wop-comboinput]');
    var panel = wrap.querySelector('[data-wop-combopanel]');
    var list = wrap.querySelector('[data-wop-combolist]');
    var moreNote = wrap.querySelector('[data-wop-combomore]');
    var emptyNote = wrap.querySelector('[data-wop-comboempty]');

    var items = [];
    var rendered = [];
    var activeIndex = -1;
    var selected = null;
    var listHasImages = false;

    var otherItem = {
      value: opts.otherLabel,
      label: opts.otherLabel,
      size: '',
      other: true,
      search: normalize(opts.otherLabel)
    };

    function showSize(item) {
      return !!item.size && normalize(item.label).indexOf(normalize(item.size)) === -1;
    }
    function displayLabel(item) {
      return item.label + (showSize(item) ? ' — ' + item.size : '');
    }

    function findByValue(value) {
      for (var i = 0; i < items.length; i++) if (items[i].value === value) return items[i];
      return null;
    }
    function findExact(text) {
      var q = normalize(text);
      if (!q) return null;
      if (normalize(otherItem.label) === q) return otherItem;
      for (var i = 0; i < items.length; i++) {
        if (normalize(displayLabel(items[i])) === q || normalize(items[i].label) === q) return items[i];
      }
      return null;
    }

    /* A refreshed catalog keeps the current choice when it still exists
       (re-pointed at the fresh object) and clears it loudly when it doesn't. */
    function setItems(next) {
      items = next || [];
      listHasImages = false;
      for (var i = 0; i < items.length; i++) {
        items[i].search = normalize(items[i].label + ' ' + (items[i].size || '') + ' ' + (items[i].sku || ''));
        if (items[i].img) listHasImages = true;
      }
      if (selected && !selected.other) {
        var fresh = findByValue(selected.value);
        if (fresh) selected = fresh;
        else clear(false);
      }
    }

    function buildOption(item, index) {
      var li = document.createElement('li');
      li.className = 'wop-combo-option';
      li.id = list.id + '-opt-' + index;
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', (selected && selected.value === item.value) ? 'true' : 'false');
      if (item.other) li.className += ' wop-combo-option--other';

      if (item.img) {
        var img = document.createElement('img');
        img.className = 'wop-combo-thumb';
        img.src = item.img;
        img.alt = '';
        img.width = 32;
        img.height = 32;
        img.setAttribute('loading', 'lazy');
        li.appendChild(img);
      } else if (listHasImages) {
        var spacer = document.createElement('span');
        spacer.className = 'wop-combo-thumb wop-combo-thumb--empty';
        spacer.setAttribute('aria-hidden', 'true');
        li.appendChild(spacer);
      }

      var text = document.createElement('span');
      text.className = 'wop-combo-text';
      text.appendChild(document.createTextNode(item.label));
      if (showSize(item)) {
        var size = document.createElement('span');
        size.className = 'wop-combo-size';
        size.textContent = item.size;
        text.appendChild(size);
      }
      li.appendChild(text);

      if (item.archived) {
        var chip = document.createElement('span');
        chip.className = 'wop-combo-chip';
        chip.textContent = 'Archived';
        li.appendChild(chip);
      }

      // mousedown would blur the input (and close the list) before click lands.
      li.addEventListener('mousedown', function (evt) { evt.preventDefault(); });
      li.addEventListener('click', function () { choose(item); });
      return li;
    }

    function render(query) {
      var raw = normalize(query).split(' ');
      var tokens = [];
      for (var t = 0; t < raw.length; t++) if (raw[t]) tokens.push(raw[t]);

      list.innerHTML = '';
      rendered = [];
      var matched = 0;
      for (var i = 0; i < items.length; i++) {
        var ok = true;
        for (var k = 0; k < tokens.length; k++) {
          if (items[i].search.indexOf(tokens[k]) === -1) { ok = false; break; }
        }
        if (!ok) continue;
        matched++;
        if (rendered.length < MAX_OPTIONS) rendered.push(items[i]);
      }
      rendered.push(otherItem);   // always reachable, outside the cap

      for (var j = 0; j < rendered.length; j++) list.appendChild(buildOption(rendered[j], j));

      var hiddenCount = matched - Math.min(matched, MAX_OPTIONS);
      moreNote.hidden = hiddenCount <= 0;
      if (hiddenCount > 0) {
        moreNote.textContent = 'Keep typing to narrow ' + hiddenCount + ' more ' + (hiddenCount === 1 ? 'match' : 'matches') + '…';
      }
      emptyNote.hidden = !(tokens.length && matched === 0);
      setActive(tokens.length ? 0 : indexOfSelected());
    }

    function indexOfSelected() {
      if (!selected) return -1;
      for (var i = 0; i < rendered.length; i++) if (rendered[i].value === selected.value) return i;
      return -1;
    }

    function setActive(index) {
      activeIndex = index;
      for (var i = 0; i < list.children.length; i++) {
        list.children[i].classList.toggle('is-active', i === index);
      }
      var el = index >= 0 ? list.children[index] : null;
      if (el) {
        input.setAttribute('aria-activedescendant', el.id);
        var top = el.offsetTop;
        var bottom = top + el.offsetHeight;
        if (top < list.scrollTop) list.scrollTop = top;
        else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    function open() {
      if (input.disabled) return;
      panel.hidden = false;   // before render(): scroll maths need a laid-out list
      input.setAttribute('aria-expanded', 'true');
      var showingSelection = input.value === (selected ? displayLabel(selected) : '');
      render(showingSelection ? '' : input.value);
    }
    function close() {
      if (panel.hidden) return;
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      list.innerHTML = '';
      rendered = [];
      activeIndex = -1;
    }

    function choose(item) {
      selected = item;
      input.value = displayLabel(item);
      input.removeAttribute('aria-invalid');
      close();
      if (opts.onSelect) opts.onSelect(item);
    }
    function clear(silent) {
      selected = null;
      input.value = '';
      close();
      if (!silent && opts.onSelect) opts.onSelect(null);
    }

    /* On blur the box shows exactly what is selected. An unchanged exact
       match is not re-committed: the series onSelect wipes the product. */
    function restore() {
      var typed = trim(input.value);
      if (!typed) {
        if (selected) clear(false);
        return;
      }
      var exact = findExact(typed);
      if (exact) {
        if (!selected || selected.value !== exact.value) choose(exact);
        else input.value = displayLabel(selected);
        return;
      }
      input.value = selected ? displayLabel(selected) : '';
    }

    function setEnabled(enabled, placeholder) {
      input.disabled = !enabled;
      if (placeholder != null) input.placeholder = placeholder;
      if (!enabled) close();
    }

    input.addEventListener('input', function () {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      render(input.value);
    });
    input.addEventListener('click', function () {
      if (panel.hidden) open(); else close();
    });
    input.addEventListener('keydown', function (evt) {
      var key = evt.key;
      if (key === 'ArrowDown' || key === 'Down') {
        evt.preventDefault();
        if (panel.hidden) open();
        else setActive(activeIndex + 1 >= rendered.length ? 0 : activeIndex + 1);
      } else if (key === 'ArrowUp' || key === 'Up') {
        evt.preventDefault();
        if (panel.hidden) { open(); setActive(rendered.length - 1); }
        else setActive(activeIndex - 1 < 0 ? rendered.length - 1 : activeIndex - 1);
      } else if (key === 'Home' || key === 'End') {
        if (panel.hidden) return;
        evt.preventDefault();
        setActive(key === 'Home' ? 0 : rendered.length - 1);
      } else if (key === 'Enter') {
        if (panel.hidden) return;
        evt.preventDefault();
        if (activeIndex >= 0 && rendered[activeIndex]) choose(rendered[activeIndex]);
      } else if (key === 'Escape' || key === 'Esc') {
        if (panel.hidden) return;
        evt.preventDefault();
        close();
        input.value = selected ? displayLabel(selected) : '';
      } else if (key === 'Tab') {
        close();
      }
    });
    input.addEventListener('blur', function () {
      close();
      restore();
    });

    return {
      wrap: wrap,
      input: input,
      setItems: setItems,
      setEnabled: setEnabled,
      clear: clear,
      close: close,
      displayLabel: displayLabel,
      getSelected: function () { return selected; }
    };
  }

  /* ── one section instance ─────────────────────────────────────────────── */

  function init(root) {
    if (root.getAttribute('data-wop-ready')) return;
    var cfgEl = root.querySelector('[data-wop-config]');
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    root.setAttribute('data-wop-ready', 'true');

    function q(sel) { return root.querySelector(sel); }
    function qa(sel) { return root.querySelectorAll(sel); }

    var form = q('[data-wop-form]');
    var panels = qa('[data-wop-step-panel]');
    var errorBox = q('[data-wop-error]');
    var submitBtn = q('[data-wop-submit]');
    var submitHTML = submitBtn.innerHTML;
    var successBox = q('[data-wop-success]');
    var passCard = q('[data-wop-pass]');
    var progressPanel = q('[data-wop-progress]');

    var field = {
      series: q('[data-wop-field="series"]'),
      product: q('[data-wop-field="product"]'),
      source: q('[data-wop-field="source"]'),
      date: q('[data-wop-field="date"]'),
      order: q('[data-wop-field="order"]'),
      first: q('[data-wop-field="first"]'),
      last: q('[data-wop-field="last"]'),
      email: q('[data-wop-field="email"]')
    };
    var seriesOtherEl = q('[data-wop-other="series"]');
    var productOtherEl = q('[data-wop-other="product"]');
    var sourceEl = q('[name="purchaseSource"]');
    var dateEl = q('[name="purchaseDate"]');
    var orderEl = q('[name="orderNumber"]');
    var firstEl = q('[name="firstName"]');
    var lastEl = q('[name="lastName"]');
    var emailEl = q('[name="email"]');
    var catalogNote = q('[data-wop-catalognote]');
    var seriesLabel = q('[data-wop-label="series"]');
    var productLabel = q('[data-wop-label="product"]');

    var step = 0;
    var catalogMode = 'loading';   // 'loading' | 'ready' | 'text'
    var catalogVersion = '';
    var complete = false;
    var gearStepComplete = false;
    var lastPercent = 0;
    var gainTimer = null;
    var sending = false;
    var attempt = null;            // { id, payload, uncertain }
    var lockedControls = null;

    dateEl.setAttribute('max', todayISO());
    dateEl.setAttribute('min', MIN_DATE);

    var REJECTIONS = {
      bad_request: 'Please check your details and try again.',
      unauthorized: 'Registration is temporarily unavailable. Please email ' + cfg.fallbackEmail + ' and we’ll register your product for you.',
      unknown_action: 'Registration is temporarily unavailable. Please email ' + cfg.fallbackEmail + ' and we’ll register your product for you.',
      rate_limited: 'Too many registrations from here in the last hour. Please try again later, or email ' + cfg.fallbackEmail + '.',
      captcha: 'Please complete the verification again.',
      missing_required: 'Please fill in all required fields.',
      invalid_email: 'Please check your email address.',
      invalid_products: 'Please check your product details.',
      invalid_date: 'Please check your purchase date — it can’t be in the future.',
      catalog_unavailable: 'The product list is temporarily unavailable. Please reload the page and try again.'
    };

    function val(el) { return el ? trim(el.value) : ''; }
    function choiceValue() {
      var checked = q('[name="marketingChoice"]:checked');
      return checked ? checked.value : '';
    }

    /* ── series / product ── */

    function buildSeriesItems(series) {
      var out = [];
      for (var i = 0; i < series.length; i++) {
        var entry = series[i];
        if (!entry || !entry.n) continue;
        out.push({ value: entry.n, label: entry.n, size: '', archived: entry.a === 0, img: entry.g || '', entry: entry });
      }
      return out;
    }
    function buildProductItems(entry) {
      var out = [];
      var list = entry && entry.p ? entry.p : [];
      var seriesArchived = !!(entry && entry.a === 0);
      for (var i = 0; i < list.length; i++) {
        var p = list[i];
        if (!p || !p.n) continue;
        out.push({
          value: p.n + '||' + (p.s || ''),
          name: p.n,
          label: p.n,
          size: p.s || '',
          archived: p.a === 0 || seriesArchived,
          sku: p.k || '',
          productId: (p.i || p.i === 0) ? String(p.i) : ''
        });
      }
      return out;
    }

    function toggleOther(el, show) {
      el.hidden = !show;
      el.disabled = !show;
      if (show) el.setAttribute('aria-required', 'true');
      else { el.removeAttribute('aria-required'); el.value = ''; el.removeAttribute('aria-invalid'); }
    }

    var productCombo = createCombobox(q('[data-wop-combo="product"]'), {
      otherLabel: cfg.otherProduct,
      onSelect: function (item) {
        var other = !!(item && item.other);
        toggleOther(productOtherEl, other);
        if (other) productOtherEl.focus();
        refresh();
      }
    });

    var seriesCombo = createCombobox(q('[data-wop-combo="series"]'), {
      otherLabel: cfg.otherSeries,
      onSelect: function (item) {
        var other = !!(item && item.other);
        toggleOther(seriesOtherEl, other);
        productCombo.clear(true);
        toggleOther(productOtherEl, other);
        productCombo.wrap.hidden = other;
        productLabel.htmlFor = other ? productOtherEl.id : productCombo.input.id;
        productCombo.input.removeAttribute('aria-invalid');

        if (!item) {
          productCombo.setItems([]);
          productCombo.setEnabled(false, PH.pickSeries);
        } else if (other) {
          productCombo.setItems([]);
          productCombo.setEnabled(false, '');
          seriesOtherEl.focus();
        } else {
          productCombo.setItems(buildProductItems(item.entry));
          productCombo.setEnabled(true, PH.product);
        }
        refresh();
      }
    });

    document.addEventListener('click', function (evt) {
      if (!seriesCombo.wrap.contains(evt.target)) seriesCombo.close();
      if (!productCombo.wrap.contains(evt.target)) productCombo.close();
    });

    function seriesIsOther() {
      var s = seriesCombo.getSelected();
      return catalogMode === 'text' || !!(s && s.other);
    }
    function productIsOther() {
      var p = productCombo.getSelected();
      return seriesIsOther() || !!(p && p.other);
    }
    function seriesName() {
      if (seriesIsOther()) return val(seriesOtherEl);
      var s = seriesCombo.getSelected();
      return s ? s.label : '';
    }
    function gearName() {
      if (productIsOther()) return val(productOtherEl);
      var p = productCombo.getSelected();
      return p ? p.label + (p.size && normalize(p.label).indexOf(normalize(p.size)) === -1 ? ' · ' + p.size : '') : '';
    }
    function dateOptional() {
      return sourceEl.value === 'gift' || sourceEl.value === 'unknown';
    }

    /* ── catalog ── */

    function readCache() {
      try {
        var version = window.sessionStorage.getItem(CACHE_POINTER);
        if (!version) return null;
        var series = JSON.parse(window.sessionStorage.getItem(CACHE_PREFIX + version) || 'null');
        return series && series.length ? { version: version, series: series } : null;
      } catch (e) {
        return null;
      }
    }
    function writeCache(version, series) {
      if (!version) return;
      try {
        window.sessionStorage.setItem(CACHE_PREFIX + version, JSON.stringify(series));
        window.sessionStorage.setItem(CACHE_POINTER, version);
      } catch (e) { /* private mode or quota: next load fetches cold */ }
    }

    function applyCatalog(series) {
      catalogMode = 'ready';
      catalogNote.hidden = true;
      seriesCombo.setItems(buildSeriesItems(series));
      seriesCombo.setEnabled(true, PH.series);
      var chosen = seriesCombo.getSelected();
      if (chosen && !chosen.other) {
        productCombo.setItems(buildProductItems(chosen.entry));
        productCombo.setEnabled(true, PH.product);
      }
      refresh();
    }

    function enterTextMode() {
      catalogMode = 'text';
      catalogNote.hidden = false;
      seriesCombo.close();
      productCombo.close();
      seriesCombo.wrap.hidden = true;
      productCombo.wrap.hidden = true;
      toggleOther(seriesOtherEl, true);
      toggleOther(productOtherEl, true);
      seriesLabel.htmlFor = seriesOtherEl.id;
      productLabel.htmlFor = productOtherEl.id;
      refresh();
    }

    function fetchJSON(url, payload, timeoutMs) {
      var controller = window.AbortController ? new AbortController() : null;
      var timer = null;
      var request = fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },   // no CORS preflight
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      }).then(function (resp) { return resp.json(); });

      var timeout = new Promise(function (resolve, reject) {
        timer = setTimeout(function () {
          if (controller) controller.abort();
          reject(new Error('timeout'));
        }, timeoutMs);
      });
      return Promise.race([request, timeout]).then(function (res) {
        clearTimeout(timer);
        return res;
      }, function (err) {
        clearTimeout(timer);
        throw err;
      });
    }

    function loadCatalog() {
      if (!cfg.url || !cfg.token) { enterTextMode(); return; }
      var cached = readCache();
      if (cached) {
        catalogVersion = cached.version;
        applyCatalog(cached.series);
      }
      fetchJSON(cfg.url, { action: 'getCatalog', token: cfg.token }, CATALOG_TIMEOUT_MS).then(function (res) {
        if (!res || !res.ok || !res.series || !res.series.length) throw new Error('catalog');
        if (catalogMode === 'ready' && res.version && res.version === catalogVersion) return;
        catalogVersion = res.version || '';
        writeCache(catalogVersion, res.series);
        applyCatalog(res.series);
      })['catch'](function () {
        if (catalogMode !== 'ready') enterTextMode();
      });
    }

    /* ── progress, owner card, review ── */

    function refresh() {
      var name = trim(val(firstEl) + ' ' + val(lastEl));
      var gear = gearName();
      var seriesOK = !!seriesName();
      var productOK = !!gear;
      var sourceOK = !!sourceEl.value;
      var dateValue = dateEl.value;
      var dateOK = !(dateEl.validity && dateEl.validity.badInput) &&
        (dateValue ? (dateValue >= MIN_DATE && dateValue <= todayISO()) : (sourceOK && dateOptional()));
      var orderOK = !!val(orderEl) || gearStepComplete;
      var emailOK = EMAIL_RE.test(val(emailEl));

      var checks = [
        [field.series, seriesOK], [field.product, productOK], [field.source, sourceOK],
        [field.date, dateOK], [field.order, orderOK], [field.first, !!val(firstEl)],
        [field.last, !!val(lastEl)], [field.email, emailOK]
      ];
      var points = choiceValue() ? 1 : 0;
      for (var i = 0; i < checks.length; i++) {
        checks[i][0].classList.toggle('is-complete', checks[i][1]);
        if (checks[i][1]) points++;
      }
      var percent = complete ? 100 : points * 10;

      q('[data-wop-fill]').style.width = percent + '%';
      q('[role="progressbar"]').setAttribute('aria-valuenow', String(percent));
      q('[data-wop-percent]').textContent = percent + '%';
      q('[data-wop-message]').textContent =
        complete ? 'All steps complete.' :
        percent === 90 ? 'Ready to finish.' :
        step === 2 ? 'One last choice.' :
        step === 1 ? 'Make it yours.' :
        !seriesOK ? (name ? 'Welcome back. Add your next product.' : 'Start with your gear.') :
        !productOK ? 'Now choose your product.' :
        (!sourceOK || !dateOK) ? 'Add purchase details.' :
        'Your gear is ready.';

      if (percent !== lastPercent) q('[data-wop-announce]').textContent = percent + '% complete.';
      if (percent > lastPercent) {
        var gain = q('[data-wop-gain]');
        gain.textContent = '+' + (percent - lastPercent) + '%';
        gain.classList.add('is-visible');
        clearTimeout(gainTimer);
        gainTimer = setTimeout(function () { gain.classList.remove('is-visible'); }, 1500);
        passCard.classList.remove('is-levelup');
        void passCard.offsetWidth;   // restart the animation
        passCard.classList.add('is-levelup');
      }
      lastPercent = percent;

      q('[data-wop-name]').textContent = name || 'Your name';
      q('[data-wop-product]').textContent = gear ? [seriesName(), gear].join(' · ') : 'Your product will appear here.';
      q('[data-wop-status]').textContent = complete ? 'Registered' : 'In the making';
      if (!complete) q('[data-wop-bottom]').textContent = 'Your card comes to life as you go';
    }

    function renderReview() {
      var dl = q('[data-wop-reviewlist]');
      dl.innerHTML = '';
      var choice = choiceValue();
      var pairs = [
        ['Product', [seriesName(), gearName()].join(' · ').replace(/^ · | · $/g, '') || 'Not provided'],
        ['Purchase', sourceEl.value ? sourceEl.options[sourceEl.selectedIndex].text : 'Not provided'],
        ['Date', val(dateEl) || 'Not provided'],
        ['Order', val(orderEl) || 'Not provided'],
        ['Name', trim(val(firstEl) + ' ' + val(lastEl)) || 'Not provided'],
        ['Email', val(emailEl) || 'Not provided'],
        ['Email offers', choice === 'yes' ? 'Yes' : choice === 'no' ? 'No' : 'Not chosen']
      ];
      for (var i = 0; i < pairs.length; i++) {
        var dt = document.createElement('dt');
        var dd = document.createElement('dd');
        dt.textContent = pairs[i][0];
        dd.textContent = pairs[i][1];
        dl.appendChild(dt);
        dl.appendChild(dd);
      }
    }

    function applySourceState() {
      var optional = dateOptional();
      dateEl.required = !optional;
      q('[data-wop-datereq]').hidden = optional;
      q('[data-wop-dateopt]').hidden = !optional;
      q('[data-wop-giftnote]').hidden = !optional;
      if (optional) dateEl.removeAttribute('aria-invalid');
    }

    /* ── steps, errors, scrolling ── */

    function scrollIntoComfort(el) {
      var offset = stickyHeaderHeight() + (progressPanel ? progressPanel.offsetHeight : 0) + 16;
      var rect = el.getBoundingClientRect();
      if (rect.top >= offset && rect.bottom <= window.innerHeight - 16) return;
      scrollToY(rect.top + (window.pageYOffset || document.documentElement.scrollTop) - offset);
    }

    function focusQuietly(el) {
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }

    function go(next, focus) {
      step = next;
      clearError();
      root.setAttribute('data-wop-step', String(step));
      each(panels, function (panel, i) { panel.hidden = i !== step; });
      each(qa('[data-wop-mark]'), function (mark, i) {
        mark.classList.toggle('is-done', i < step);
        if (i === step) mark.setAttribute('aria-current', 'step');
        else mark.removeAttribute('aria-current');
      });
      refresh();
      renderReview();
      if (focus !== false) {
        var heading = panels[step].querySelector('.wop-step-title');
        focusQuietly(heading);
        scrollIntoComfort(heading);
      }
    }

    function clearError() {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    /* Field errors sit under the field; form-level errors above the step's
       buttons, next to the action that was just pressed. */
    function fail(message, el) {
      errorBox.textContent = message;
      var host = el ? (closest(el, '.wop-field') || closest(el, '[data-wop-choices]')) : null;
      if (host) {
        if (host.hasAttribute('data-wop-choices')) host.parentNode.insertBefore(errorBox, host.nextSibling);
        else host.appendChild(errorBox);
        if (host.hasAttribute('data-wop-choices')) {
          each(host.querySelectorAll('input'), function (radio) { radio.setAttribute('aria-invalid', 'true'); });
        } else {
          el.setAttribute('aria-invalid', 'true');
        }
      } else {
        var actions = panels[step].querySelector('[data-wop-actions]');
        actions.parentNode.insertBefore(errorBox, actions);
      }
      errorBox.hidden = false;
      var target = el && !el.disabled ? el : errorBox;
      focusQuietly(target);
      scrollIntoComfort(host || errorBox);
    }

    function check(index) {
      if (index === 0) {
        if (catalogMode === 'loading') return { message: 'The product list is still loading. Please try again in a moment.' };
        if (catalogMode === 'text') {
          if (!val(seriesOtherEl)) return { message: 'Please enter your series.', el: seriesOtherEl };
          if (!val(productOtherEl)) return { message: 'Please enter your product.', el: productOtherEl };
        } else {
          var s = seriesCombo.getSelected();
          if (!s) return { message: 'Please choose your series.', el: seriesCombo.input };
          if (s.other && !val(seriesOtherEl)) return { message: 'Please tell us which series you own.', el: seriesOtherEl };
          if (s.other) {
            if (!val(productOtherEl)) return { message: 'Please tell us which product you own.', el: productOtherEl };
          } else {
            var p = productCombo.getSelected();
            if (!p) return { message: 'Please choose your product.', el: productCombo.input };
            if (p.other && !val(productOtherEl)) return { message: 'Please tell us which product you own.', el: productOtherEl };
          }
        }
        if (!sourceEl.value) return { message: 'Please choose where you got it.', el: sourceEl };
        if (dateEl.validity && dateEl.validity.badInput) return { message: 'Please enter the purchase date as a real date.', el: dateEl };
        var date = val(dateEl);
        if (!date && !dateOptional()) return { message: 'Please enter your purchase date.', el: dateEl };
        if (date && (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date > todayISO() || date < MIN_DATE)) {
          return { message: 'Choose a real purchase date between 2010 and today.', el: dateEl };
        }
      } else if (index === 1) {
        if (!val(firstEl)) return { message: 'Please enter your first name.', el: firstEl };
        if (!val(lastEl)) return { message: 'Please enter your last name.', el: lastEl };
        if (!val(emailEl)) return { message: 'Please enter your email address.', el: emailEl };
        if (!EMAIL_RE.test(val(emailEl))) return { message: 'Please enter a valid email address, like you@example.com.', el: emailEl };
      } else if (index === 2) {
        if (!choiceValue()) {
          return { message: 'Choose whether you’d like emails with offers, or select “No thanks” to continue.', el: q('[name="marketingChoice"]') };
        }
      }
      return null;
    }

    function advance() {
      var problem = check(step);
      if (problem) { fail(problem.message, problem.el); return; }
      if (step === 0) gearStepComplete = true;
      go(step + 1);
    }

    /* ── submit ── */

    function captchaValue() {
      var el = q('[name="cf-turnstile-response"]');
      return el ? el.value : '';
    }
    function resetCaptcha() {
      if (cfg.sitekey && window.turnstile && window.turnstile.reset) {
        try { window.turnstile.reset(); } catch (e) { /* widget not rendered */ }
      }
    }

    function buildPayload(id) {
      var seriesOther = seriesIsOther();
      var productOther = productIsOther();
      var s = seriesCombo.getSelected();
      var p = productCombo.getSelected();
      return {
        action: 'submit',
        submissionId: id,
        hp: val(q('[name="hp"]')),
        captchaToken: captchaValue(),
        firstName: val(firstEl),
        lastName: val(lastEl),
        email: val(emailEl),
        marketingConsent: choiceValue() === 'yes',
        consentCopyVersion: cfg.consentCopyVersion,
        formVersion: cfg.formVersion,
        sourceUrl: location.origin + location.pathname,
        products: [{
          series: seriesOther ? cfg.otherSeries : s.value,
          productName: productOther ? cfg.otherProduct : p.name,
          sku: productOther ? '' : p.sku,
          productId: productOther ? '' : p.productId,
          seriesOther: seriesOther ? val(seriesOtherEl) : '',
          productOther: productOther ? val(productOtherEl) : '',
          purchaseSource: dateOptional() ? cfg.giftSource : sourceEl.value,
          purchaseDate: val(dateEl),
          orderNumber: val(orderEl)
        }]
      };
    }

    function lockForm() {
      if (!lockedControls) {
        lockedControls = [];
        each(form.querySelectorAll('input, select, button'), function (el) {
          if (el.name === 'cf-turnstile-response') return;
          lockedControls.push([el, el.disabled]);
        });
      }
      for (var i = 0; i < lockedControls.length; i++) lockedControls[i][0].disabled = true;
    }
    function unlockForm() {
      if (lockedControls) {
        for (var i = 0; i < lockedControls.length; i++) lockedControls[i][0].disabled = lockedControls[i][1];
      }
      lockedControls = null;
      submitBtn.innerHTML = submitHTML;
    }

    /* No receipt and no definite rejection: the registration may or may not
       exist. The payload stays frozen so Retry replays the same submissionId,
       which the backend answers with the original registration if it landed. */
    function uncertain(message) {
      attempt.uncertain = true;
      resetCaptcha();
      submitBtn.disabled = false;
      submitBtn.textContent = 'Retry confirmation';
      fail(message + ' Retry checks the same registration, so it can’t be registered twice. If this keeps happening, email ' +
        cfg.fallbackEmail + ' and mention request ' + attempt.id + '.');
    }

    function send() {
      sending = true;
      clearError();
      lockForm();
      submitBtn.textContent = 'Sending…';
      form.setAttribute('aria-busy', 'true');
      q('[data-wop-announce]').textContent = 'Sending your registration.';

      var body = {};
      for (var key in attempt.payload) {
        if (Object.prototype.hasOwnProperty.call(attempt.payload, key)) body[key] = attempt.payload[key];
      }
      body.token = cfg.token;
      body.captchaToken = captchaValue() || body.captchaToken;

      function done() {
        sending = false;
        form.removeAttribute('aria-busy');
      }

      fetchJSON(cfg.url, body, SUBMIT_TIMEOUT_MS).then(function (res) {
        done();
        if (res && res.ok === true && typeof res.registrationId === 'string' && trim(res.registrationId)) {
          showComplete(trim(res.registrationId), res.email || attempt.payload.email);
        } else if (res && res.ok === false && Object.prototype.hasOwnProperty.call(REJECTIONS, res.error)) {
          attempt = null;
          unlockForm();
          if (res.error === 'captcha') resetCaptcha();
          fail(REJECTIONS[res.error]);
        } else {
          uncertain('We didn’t receive a registration receipt.');
        }
      })['catch'](function () {
        done();
        uncertain('We couldn’t confirm whether your registration was received.');
      });
    }

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      if (sending || complete) return;
      if (step < 2) { advance(); return; }
      if (attempt && attempt.uncertain) { send(); return; }

      for (var i = 0; i < 3; i++) {
        var problem = check(i);
        if (problem) {
          if (i !== step) go(i, false);
          fail(problem.message, problem.el);
          return;
        }
      }
      if (!cfg.url || !cfg.token) {
        fail('Registration is temporarily unavailable. Please email ' + cfg.fallbackEmail + ' and we’ll register your product for you.');
        return;
      }
      if (cfg.sitekey && !captchaValue()) {
        fail('Please complete the verification before continuing.');
        return;
      }
      var id = newUuid();
      attempt = { id: id, payload: buildPayload(id), uncertain: false };
      send();
    });

    function showComplete(registrationId, email) {
      complete = true;
      root.setAttribute('data-wop-step', 'done');
      q('[data-wop-receipt]').textContent = registrationId;
      q('[data-wop-successemail]').textContent = email;
      q('[data-wop-bottom]').textContent = 'Registration ID · ' + registrationId;
      form.hidden = true;
      successBox.hidden = false;
      each(qa('[data-wop-mark]'), function (mark) {
        mark.classList.add('is-done');
        mark.removeAttribute('aria-current');
      });
      refresh();
      focusQuietly(successBox);
      scrollIntoComfort(passCard);
      celebrate();
    }

    function celebrate() {
      if (reducedMotion()) return;
      var wrap = q('[data-wop-confetti]');
      wrap.innerHTML = '';
      for (var i = 0; i < 32; i++) {
        var piece = document.createElement('i');
        piece.style.setProperty('--x', ((Math.random() - 0.5) * 650) + 'px');
        piece.style.setProperty('--y', (100 + Math.random() * 450) + 'px');
        piece.style.setProperty('--r', (Math.random() * 900) + 'deg');
        piece.style.animationDelay = (Math.random() * 0.15) + 's';
        wrap.appendChild(piece);
      }
      setTimeout(function () { wrap.innerHTML = ''; }, 2100);
    }

    /* Keeps name and email; everything about the registered product resets. */
    q('[data-wop-again]').addEventListener('click', function () {
      if (sending) return;
      attempt = null;
      complete = false;
      gearStepComplete = false;
      unlockForm();
      resetCaptcha();

      if (catalogMode === 'text') {
        seriesOtherEl.value = '';
        productOtherEl.value = '';
      } else {
        seriesCombo.clear(false);
      }
      sourceEl.value = '';
      dateEl.value = '';
      orderEl.value = '';
      applySourceState();
      each(qa('[name="marketingChoice"]'), function (radio) {
        radio.checked = false;
        radio.removeAttribute('aria-invalid');
      });
      q('[data-wop-review]').open = false;

      successBox.hidden = true;
      form.hidden = false;
      go(0);
    });

    /* ── wiring ── */

    each(qa('[data-wop-next]'), function (btn) { btn.addEventListener('click', advance); });
    each(qa('[data-wop-back]'), function (btn) {
      btn.addEventListener('click', function () { go(Math.max(0, step - 1)); });
    });
    q('[data-wop-edit]').addEventListener('click', function () { go(0); });
    sourceEl.addEventListener('change', applySourceState);

    function onEdit(evt) {
      var target = evt.target;
      if (!errorBox.hidden && !(attempt && attempt.uncertain)) clearError();
      if (target && target.removeAttribute) target.removeAttribute('aria-invalid');
      if (target && target.name === 'marketingChoice') {
        each(qa('[name="marketingChoice"]'), function (radio) { radio.removeAttribute('aria-invalid'); });
      }
      refresh();
      if (evt.type === 'change') renderReview();
    }
    form.addEventListener('input', onEdit);
    form.addEventListener('change', onEdit);

    /* First click or focus inside the form narrows the photo column (CSS,
       desktop only). It stays narrow; flipping back on blur would jump. */
    function engage() {
      if (root.className.indexOf('is-engaged') === -1) root.classList.add('is-engaged');
    }
    form.addEventListener('focusin', engage);
    form.addEventListener('pointerdown', engage);

    go(0, false);
    loadCatalog();
  }

  /* ── boot ─────────────────────────────────────────────────────────────── */

  var ticking = false;
  function syncStickyOffset() {
    ticking = false;
    var top = stickyHeaderBottom() + 'px';
    each(document.querySelectorAll('[data-wop]'), function (root) {
      root.style.setProperty('--wop-sticky-top', top);
    });
  }
  function requestSync() {
    if (ticking) return;
    ticking = true;
    (window.requestAnimationFrame || setTimeout)(syncStickyOffset);
  }

  function boot() {
    each(document.querySelectorAll('[data-wop]'), init);
    syncStickyOffset();
  }

  window.addEventListener('scroll', requestSync, { passive: true });
  window.addEventListener('resize', requestSync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
